import { Card, CardContent } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-2 min-w-[140px]">
      <p className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-1">+{label} km</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-mono flex justify-between gap-3" style={{ color: p.color }}>
          <span>{p.name}:</span>
          <span className="font-bold">{typeof p.value === 'number' ? p.value.toFixed(1) : p.value}%</span>
        </p>
      ))}
    </div>
  );
};

export default function PredictiveChart({ title, data, currentKm, type }) {
  if (!data || data.length === 0) return null;

  const isMaintained = type === "maintained";
  const Icon = isMaintained ? TrendingUp : TrendingDown;
  const mainColor = isMaintained ? "#10B981" : "#EF4444";
  const secondColor = isMaintained ? "#0EA5E9" : "#F97316";
  const thirdColor = isMaintained ? "#8B5CF6" : "#F59E0B";

  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden" data-testid={`predictive-chart-${type}`}>
      <div className="border-b border-slate-200 p-3 bg-slate-50/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={14} className={isMaintained ? "text-emerald-500" : "text-red-500"} />
          <span className="text-sm font-bold uppercase tracking-wider text-slate-500">{title}</span>
        </div>
        {currentKm !== undefined && (
          <span className="text-sm font-mono text-slate-400">Current: {currentKm?.toLocaleString()} km</span>
        )}
      </div>
      <CardContent className="p-4">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis
              dataKey="km"
              tick={{ fontSize: 10, fill: '#94A3B8' }}
              tickFormatter={(v) => `+${(v / 1000).toFixed(0)}k`}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#94A3B8' }}
              domain={[0, 100]}
              tickFormatter={(v) => `${v}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '10px' }}
              iconType="line"
            />
            <Line
              type="monotone"
              dataKey="health"
              stroke={mainColor}
              strokeWidth={2.5}
              dot={{ r: 4, fill: mainColor }}
              activeDot={{ r: 6 }}
              name="Health"
            />
            <Line
              type="monotone"
              dataKey="performance"
              stroke={secondColor}
              strokeWidth={1.5}
              dot={{ r: 3 }}
              strokeDasharray="5 5"
              name="Performance"
            />
            <Line
              type="monotone"
              dataKey="fuel_efficiency"
              stroke={thirdColor}
              strokeWidth={1.5}
              dot={{ r: 3 }}
              strokeDasharray="3 3"
              name="Fuel Efficiency"
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Data table */}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-1.5 text-sm font-bold uppercase tracking-widest text-slate-400">Distance</th>
                <th className="text-right py-1.5 text-sm font-bold uppercase tracking-widest text-slate-400">Health</th>
                <th className="text-right py-1.5 text-sm font-bold uppercase tracking-widest text-slate-400">Perf.</th>
                <th className="text-right py-1.5 text-sm font-bold uppercase tracking-widest text-slate-400">Fuel Eff.</th>
                <th className="text-right py-1.5 text-sm font-bold uppercase tracking-widest text-slate-400">Emission</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="py-1.5 font-mono text-slate-600">+{(d.km / 1000).toFixed(0)}k km</td>
                  <td className={`py-1.5 text-right font-mono font-bold ${d.health >= 70 ? 'text-emerald-600' : d.health >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                    {d.health?.toFixed(1)}%
                  </td>
                  <td className="py-1.5 text-right font-mono text-slate-500">{d.performance?.toFixed(1)}%</td>
                  <td className="py-1.5 text-right font-mono text-slate-500">{d.fuel_efficiency?.toFixed(1)}%</td>
                  <td className="py-1.5 text-right font-mono text-slate-500">{d.emission_quality?.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
